<form [formGroup]="form" class="p-20 fx-column gap-16">
  <app-progress-bar class="pb-8" [loading]="loading()" mode="indeterminate"></app-progress-bar>

  <div class="fx-column">
    <span class="page-title">
      {{ (mode === 'edit' ? 'INTEGRATIONS.EDIT_TITLE' : 'INTEGRATIONS.CREATE_TITLE') | translate }}
    </span>
    @if (mode === 'create') {
      <span class="page-subtitle">
        {{ 'INTEGRATIONS.CREATE_SUBTITLE' | translate }}
      </span>
    }
  </div>

  <mat-divider class="mv-8"/>

  <!-- Edit-mode notice: only rename is wired today -->
  @if (mode === 'edit') {
    <div class="fx-flex-100 fx-row align-items-center justify-content-start">
      <mat-icon class="light mr-10 fx-shrink-0" fontSet="material-icons-outlined">info</mat-icon>
      <span class="small light">{{ 'INTEGRATIONS.EDIT_NAME_ONLY_NOTICE' | translate }}</span>
    </div>
  }

  <!-- Name (common) -->
  <div class="fx-flex-100 fx-row">
    <app-input class="fx-flex-100" type="text"
               [label]="('INTEGRATIONS.WEBSITE.NAME' | translate)"
               [placeholder]="('INTEGRATIONS.WEBSITE.NAME' | translate) + '...'"
               formControlName="name"
               [error]="inputError(form.get('name'), { required: 'INTEGRATIONS.WEBSITE.NAME_REQUIRED' })">
    </app-input>
  </div>

  <mat-divider class="mv-8"/>

  <!-- Type selection (create only) -->
  @if (mode === 'create') {
    <div class="fx-flex-100 fx-row align-items-stretch justify-content-center">
      @for (m of crawlerModes; track m.value) {
        <div class="fx-flex ph-8">
          <mat-card class="mode-card clickable h-100" [class.disabled]="!isModeEnabled(m.value)"
                    (click)="setCrawlerMode(m.value)" [class.selected]="crawlerMode === m.value">
            <div class="fx-row align-items-center justify-content-between mb-8">
              <div class="fx-row align-items-center justify-content-start">
                <mat-icon class="dark mr-8" fontSet="material-icons-outlined">{{ m.icon }}</mat-icon>
                <span class="text bold dark">{{ m.label | translate }}</span>
              </div>
              <mat-icon [class]="crawlerMode === m.value ? 'main' : 'light'" fontSet="material-icons-outlined">
                {{ crawlerMode === m.value ? 'check_circle' : 'radio_button_unchecked' }}
              </mat-icon>
            </div>
            <span class="text">{{ (isExplorationNotEnabled(m.value) ? 'INTEGRATIONS.BACKLINK_CRAWLER.NOT_ENABLED' : m.hint) | translate }}</span>
          </mat-card>
        </div>
      }
    </div>
  }

  <!-- SINGLE URL -->
  @if (crawlerMode === 'single' && singleGroup) {
    <ng-container [formGroup]="singleGroup">
      <div class="fx-flex-100">
        <app-input type="url" [label]="'INTEGRATIONS.WEBSITE.URL' | translate"
                   [help]="'INTEGRATIONS.WEBSITE.URL_TEXT' | translate"
                   [placeholder]="'INTEGRATIONS.WEBSITE.URL_PLACEHOLDER' | translate"
                   formControlName="url"
                   [error]="inputError(singleGroup.get('url'), {
                       required: 'INTEGRATIONS.WEBSITE.URL_REQUIRED',
                       url: 'INTEGRATIONS.WEBSITE.URL_FORMAT',
                       notPublic: 'ERRORS.URL_NOT_PUBLIC'
                     })">
        </app-input>
      </div>
    </ng-container>
  }

  <!-- DISCOVERY -->
  @if (crawlerMode === 'discovery' && discoveryGroup) {
    <ng-container [formGroup]="discoveryGroup">
      @if (mode === 'create') {
        <div class="fx-flex-100 fx-row align-items-center justify-content-start">
          <app-button-toggle class="fx-flex-100" [buttons]="discoveryStrategies"
                             (change)="onDiscoveryStrategyChange($event)"></app-button-toggle>
        </div>
        <div class="fx-flex-100 fx-row align-items-center justify-content-start">
          <span class="small light">{{ 'INTEGRATIONS.BACKLINK_CRAWLER.STRATEGY_HINT' | translate }}</span>
        </div>
      }

      <app-input type="url" [label]="'INTEGRATIONS.WEBSITE.URL' | translate"
                 [help]="'INTEGRATIONS.WEBSITE.URL_TEXT' | translate"
                 [placeholder]="'INTEGRATIONS.WEBSITE.URL_PLACEHOLDER' | translate"
                 formControlName="url"
                 [error]="inputError(discoveryGroup.get('url'), {
                       required: 'INTEGRATIONS.WEBSITE.URL_REQUIRED',
                       url: 'INTEGRATIONS.WEBSITE.URL_FORMAT',
                       notPublic: 'ERRORS.URL_NOT_PUBLIC'
                     })">
      </app-input>

      @if (discoveryStrategy !== 'AMAZON') {
        <div class="fx-flex-100 fx-column">
          <app-select [labelOutside]="true" [label]="'INTEGRATIONS.BACKLINK_CRAWLER.DEPTH' | translate"
                      [help]="'INTEGRATIONS.BACKLINK_CRAWLER.DEPTH_HELP' | translate"
                      [options]="depthLevels" [multiple]="false" [emitOnInit]="false"
                      formControlName="depth"
                      [error]="inputError(discoveryGroup.get('depth'), {
                            required: 'INTEGRATIONS.BACKLINK_CRAWLER.DEPTH_OUT_OF_RANGE'
                          }, {
                          max: MAX_DISCOVERY_DEPTH
                          })">
          </app-select>
        </div>
      }

      @if (balance(); as allowance) {
        @if (allowance.subscribed > 0) {
          <!-- the year's exploration allowance: used, held by running explorations, left -->
          <div class="fx-flex-100 fx-column gap-5 allowance">
            <span class="small light bold">{{ 'INTEGRATIONS.BACKLINK_CRAWLER.ALLOWANCE.TITLE' | translate: { year: allowance.period } }}</span>
            <div class="allowance-gauge" role="img"
                 [attr.aria-label]="'INTEGRATIONS.BACKLINK_CRAWLER.ALLOWANCE.SUMMARY' | translate: allowance">
              <span class="consumed" [style.width.%]="balanceShare('consumed')"></span>
              <span class="reserved" [style.width.%]="balanceShare('reserved')"></span>
            </div>
            <span class="small light">{{ 'INTEGRATIONS.BACKLINK_CRAWLER.ALLOWANCE.SUMMARY' | translate: allowance }}</span>
          </div>
        }
      }

      <div class="fx-flex-100 fx-column">
        <app-input type="number" [label]="'INTEGRATIONS.BACKLINK_CRAWLER.MAX_PAGES' | translate"
                   [help]="'INTEGRATIONS.BACKLINK_CRAWLER.MAX_PAGES_HELP' | translate"
                   [min]="MAX_PAGES_OPTIONS_MIN" [max]="MAX_PAGES_OPTIONS_MAX"
                   formControlName="maxPages"
                   [error]="inputError(discoveryGroup.get('maxPages'), {
                            required: 'INTEGRATIONS.BACKLINK_CRAWLER.MAX_PAGES_OUT_OF_RANGE',
                            min: 'INTEGRATIONS.BACKLINK_CRAWLER.MAX_PAGES_OUT_OF_RANGE',
                            max: 'INTEGRATIONS.BACKLINK_CRAWLER.MAX_PAGES_OUT_OF_RANGE'
                          }, {
                          min: MAX_PAGES_OPTIONS_MIN.toLocaleString(),
                          max: MAX_PAGES_OPTIONS_MAX.toLocaleString()
                        })">
        </app-input>
        @if (exceedsBalance()) {
          <span class="small orange mt-5">{{ 'INTEGRATIONS.BACKLINK_CRAWLER.ALLOWANCE.CAPPED' | translate: { available: balance()?.available } }}</span>
        }
      </div>
    </ng-container>
  }

  <!-- EXCEL -->
  @if (crawlerMode === 'excel' && excelGroup) {
    <ng-container [formGroup]="excelGroup">
      <div class="fx-flex-100 fx-row align-items-center justify-content-start">
        <span class="text">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.EXCEL_FILE_TEXT' | translate }}</span>
      </div>

      <div class="fx-flex-100 fx-row align-items-center justify-content-start file-upload">
        <div class="file-upload-container fx-flex-100"
             [class.has-error]="inputError(excelGroup.get('excelFile'), {}).value || excelGroup.get('excelFile')?.errors">
          <input type="file" #fileInput accept=".xlsx,.xls" (change)="onFileSelected($event)" style="display: none;"
                 [disabled]="form.disabled">

          @if (!excelFileName()) {
            <div class="file-upload-placeholder fx-row align-items-center justify-content-center"
                 (click)="!form.disabled && fileInput.click()">
              <mat-icon class="main mr-10" fontSet="material-icons-outlined">cloud_upload</mat-icon>
              <span class="text main">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.UPLOAD_EXCEL' | translate }}</span>
            </div>
          }

          @if (excelFileName()) {
            <div class="file-upload-selected fx-row align-items-center justify-content-between">
              <div class="fx-row align-items-center justify-content-start">
                <mat-icon class="main mr-10" fontSet="material-icons-outlined">insert_drive_file</mat-icon>
                <span class="text main">{{ excelFileName() }}</span>
              </div>
              @if (!form.disabled) {
                <mat-icon class="red clickable" fontSet="material-icons-outlined"
                          (click)="removeFile(fileInput)">close
                </mat-icon>
              }
            </div>
          }
        </div>
      </div>

      <!-- URL column: a loading state while the file is parsed, then a dropdown of
           the headers detected in the file, or a free-text fallback (legacy .xls,
           unreadable file, or edit mode). -->
      @if (parsingColumns()) {
        <div class="fx-flex-100 fx-row align-items-center justify-content-start">
          <app-progress-spinner mode="indeterminate" class="mr-10"></app-progress-spinner>
          <span class="small light">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.COLUMN_PARSING' | translate }}</span>
        </div>
      } @else if (excelColumns().length > 0) {
        <div class="fx-flex-100 fx-column">
          <app-select [labelOutside]="true" [label]="'INTEGRATIONS.WEBSITE_CRAWLER.URL_COLUMN' | translate"
                      [help]="'INTEGRATIONS.WEBSITE_CRAWLER.URL_COLUMN_SELECT_HINT' | translate"
                      [options]="excelColumns()" [multiple]="false" [search]="true" [emitOnInit]="false"
                      formControlName="urlColumn"
                      [error]="inputError(excelGroup.get('urlColumn'), { required: 'INTEGRATIONS.WEBSITE_CRAWLER.COLUMN_REQUIRED' })">
          </app-select>
        </div>
      } @else {
        <div class="fx-flex-100 fx-column">
          @if (columnParseFailed()) {
            <div class="fx-flex-100 fx-row align-items-center justify-content-start">
              <mat-icon class="light mr-10" fontSet="material-icons-outlined">info</mat-icon>
              <span class="small light">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.COLUMN_PARSE_FAILED' | translate }}</span>
            </div>
          }
          <app-input type="text" [label]="'INTEGRATIONS.WEBSITE_CRAWLER.URL_COLUMN' | translate"
                     [help]="'INTEGRATIONS.WEBSITE_CRAWLER.URL_COLUMN_TEXT' | translate"
                     [placeholder]="'INTEGRATIONS.WEBSITE_CRAWLER.URL_COLUMN_PLACEHOLDER' | translate"
                     formControlName="urlColumn"
                     [error]="inputError(excelGroup.get('urlColumn'), { required: 'INTEGRATIONS.WEBSITE_CRAWLER.COLUMN_REQUIRED' })">
          </app-input>
        </div>
      }
    </ng-container>
  }

  <mat-divider class="mv-8"/>

  <!-- Recurrence (common, all types) -->
  <div class="fx-flex-100">
    <app-select initial="none" [labelOutside]="true" [label]="'INTEGRATIONS.WEBSITE.RECURRENCE' | translate"
                [options]="recurrences"
                [multiple]="false" [emitOnInit]="false" formControlName="recurrence"
                [error]="inputError(form.get('recurrence'), { required: 'INTEGRATIONS.WEBSITE.RECURRENCE_REQUIRED' })">
    </app-select>
  </div>

  <!-- Countries (common, all types — only DISCOVERY expands + uses them server-side) -->
  <div class="fx-flex-100">
    <app-territory-select [label]="'INTEGRATIONS.BACKLINK_CRAWLER.COUNTRIES_TO_INCLUDE' | translate"
                          [help]="'INTEGRATIONS.BACKLINK_CRAWLER.COUNTRIES_TO_INCLUDE_HINT' | translate"
                          [labelOutside]="true" [multiple]="true" [search]="true"
                          [emitOnInit]="false" formControlName="countryCodes"></app-territory-select>
  </div>

  <mat-divider class="fx-flex-100 mv-8"></mat-divider>

  <!-- Advanced config (common, all types), platform team only -->
  @if (canTuneCapture()) {
  <div class="fx-row align-items-center justify-content-between clickable fx-flex-100"
       (click)="showAdvancedOptions.set(!showAdvancedOptions())">
    <div class="fx-flex fx-row align-items-center justify-content-start">
      <mat-icon class="dark mr-10" fontSet="material-icons-outlined">settings</mat-icon>
      <span class="text dark">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.ADVANCED_SETTINGS' | translate }}</span>
    </div>
    <mat-icon class="light" fontSet="material-icons-outlined">
      {{ showAdvancedOptions() ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
    </mat-icon>
  </div>

  @if (showAdvancedOptions()) {
    <div class="fx-flex-100 fx-row align-items-center justify-content-start">
      <mat-icon class="light mr-10" fontSet="material-icons-outlined">info</mat-icon>
      <span class="small light">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.ADVANCED_SETTINGS_HINT' | translate }}</span>
    </div>
    <div class="fx-row fx-wrap border-left pl-8" formGroupName="config">
      <div class="fx-flex-100 fx-row align-items-center justify-content-between">
        <div class="fx-row align-items-center justify-content-start mr-8">
          <span class="small bold">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.FORMAT' | translate }}</span>
          <app-help-icon class="ml-5" [text]="'INTEGRATIONS.WEBSITE_CRAWLER.FORMAT_HELP' | translate"></app-help-icon>
        </div>
        <app-select [label]="'INTEGRATIONS.WEBSITE_CRAWLER.FORMAT' | translate" [options]="formatOptions"
                    [multiple]="false" [emitOnInit]="false" formControlName="screenshotFormat" style="width:120px;">
        </app-select>
      </div>

      @if (screenshotFormat === 'jpeg') {
        <div class="fx-flex-100 fx-row align-items-center justify-content-between mt-5">
          <div class="fx-row align-items-center justify-content-start mr-8">
            <span class="small bold">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.QUALITY' | translate }}</span>
            <app-help-icon class="ml-5" [text]="'INTEGRATIONS.WEBSITE_CRAWLER.QUALITY_HELP' | translate"></app-help-icon>
          </div>
          <app-input type="number" formControlName="quality" [min]="1" [max]="100" style="width:120px;"></app-input>
        </div>
      }

      <div formGroupName="viewport" class="fx-flex-100 fx-row fx-wrap">
        <div class="fx-flex-100 fx-row align-items-center justify-content-between mt-5">
          <div class="fx-row align-items-center justify-content-start mr-8">
            <span class="small bold">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.WIDTH' | translate }}</span>
            <app-help-icon class="ml-5" [text]="'INTEGRATIONS.WEBSITE_CRAWLER.WIDTH_HELP' | translate"></app-help-icon>
          </div>
          <app-input type="number" formControlName="width" [min]="320" [max]="3840" style="width:120px;"></app-input>
        </div>
        <div class="fx-flex-100 fx-row align-items-center justify-content-between mt-5">
          <div class="fx-row align-items-center justify-content-start mr-8">
            <span class="small bold">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.HEIGHT' | translate }}</span>
            <app-help-icon class="ml-5" [text]="'INTEGRATIONS.WEBSITE_CRAWLER.HEIGHT_HELP' | translate"></app-help-icon>
          </div>
          <app-input type="number" formControlName="height" [min]="240" [max]="2160" style="width:120px;"></app-input>
        </div>
      </div>

      <div class="fx-flex-100 fx-row align-items-center justify-content-between mt-5">
        <div class="fx-row align-items-center justify-content-start mr-8">
          <span class="small bold">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.CONCURRENCY' | translate }}</span>
          <app-help-icon class="ml-5" [text]="'INTEGRATIONS.WEBSITE_CRAWLER.CONCURRENCY_HELP' | translate"></app-help-icon>
        </div>
        <app-input type="number" formControlName="concurrency" [min]="1" [max]="5" style="width:120px;"></app-input>
      </div>

      <div class="fx-flex-100 fx-row align-items-center justify-content-between mt-5">
        <div class="fx-row align-items-center justify-content-start mr-8">
          <span class="small bold">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.DELAY' | translate }}</span>
          <app-help-icon class="ml-5" [text]="'INTEGRATIONS.WEBSITE_CRAWLER.DELAY_HELP' | translate"></app-help-icon>
        </div>
        <app-input type="number" formControlName="delay" [min]="0" [max]="10000" style="width:120px;"></app-input>
      </div>

      <div class="fx-flex-100 fx-row align-items-center justify-content-between mt-5">
        <div class="fx-row align-items-center justify-content-start mr-8">
          <span class="small bold">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.BATCH_SIZE' | translate }}</span>
          <app-help-icon class="ml-5" [text]="'INTEGRATIONS.WEBSITE_CRAWLER.BATCH_SIZE_HELP' | translate"></app-help-icon>
        </div>
        <app-input type="number" formControlName="batchSize" [min]="10" [max]="500" style="width:120px;"></app-input>
      </div>

      <div class="fx-flex-100 fx-row align-items-center justify-content-between mt-5">
        <div class="fx-row align-items-center justify-content-start mr-8">
          <span class="small bold">{{ 'INTEGRATIONS.WEBSITE_CRAWLER.PAGE_TIMEOUT' | translate }}</span>
          <app-help-icon class="ml-5" [text]="'INTEGRATIONS.WEBSITE_CRAWLER.PAGE_TIMEOUT_HELP' | translate"></app-help-icon>
        </div>
        <app-input type="number" formControlName="pageTimeout" [min]="5000" [max]="240000" style="width:140px;"></app-input>
      </div>
    </div>
  }

  <mat-divider class="fx-flex-100 mv-8"></mat-divider>
  }

  <div class="fx-row align-items-center justify-content-center fx-flex-100">
    <button type="button" class="button fx-flex mr-10" (click)="onCancel()" [class.disabled]="loading()">
      {{ 'ACTIONS.CANCEL' | translate }}
    </button>
    <span class="fx-flex ml-10 fx-row" [matTooltip]="'INTEGRATIONS.FORM_INVALID' | translate"
          [matTooltipDisabled]="!form.invalid" [matTooltipClass]="{'uzai-tooltip': true}">
      @if (mode === 'edit') {
        <button type="button" class="main-button fx-flex-100" (click)="submit()"
                [class.disabled]="loading()" [disabled]="loading()">
          {{ 'ACTIONS.UPDATE' | translate }}
        </button>
      } @else {
        <button type="button" class="button fx-flex-100 mr-10" (click)="submit(false)"
                [class.disabled]="loading()" [disabled]="loading()">
          {{ 'ACTIONS.CREATE' | translate }}
        </button>
        <button type="button" class="main-button fx-flex-100" (click)="submit(true)"
                [class.disabled]="loading()" [disabled]="loading()">
          {{ 'ACTIONS.CREATE_AND_LAUNCH' | translate }}
        </button>
      }
      </span>
  </div>
</form>
